"use client";

import type { ReactNode } from "react";
import { memo, useRef } from "react";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import type { ApiDropReferencedNFT } from "@/generated/models/ApiDropReferencedNFT";
import DropPfp from "@/components/drops/create/utils/DropPfp";
import DropAuthor from "@/components/drops/create/utils/author/DropAuthor";
import Link from "next/link";
import type { ProfileMinWithoutSubs } from "@/helpers/ProfileTypes";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useRouter } from "next/navigation";
import { getWavePathRoute, getWaveRoute } from "@/helpers/navigation.helpers";
import DropPartContent from "./DropPartContent";
import { DropPartSize } from "./DropPart.types";

interface DropPartPropsMedia {
  readonly mimeType: string;
  readonly mediaSrc: string;
}

interface DropPartPropsWave {
  readonly id: string | null;
  readonly name: string;
  readonly image: string | null;
}

interface DropPartProps {
  readonly dropId?: string | undefined;
  readonly profile: ProfileMinWithoutSubs;
  readonly dropTitle: string | null;
  readonly mentionedUsers: Array<ApiDropMentionedUser>;
  readonly mentionedWaves: Array<ApiMentionedWave>;
  readonly referencedNfts: Array<ApiDropReferencedNFT>;
  readonly partContent: string | null;
  readonly partMedias: DropPartPropsMedia[];
  readonly createdAt: number;
  readonly wave: DropPartPropsWave | null;
  readonly size?: DropPartSize | undefined;
  readonly totalPartsCount?: number | undefined;
  readonly currentPartCount?: number | undefined;
  readonly smallMenuIsShown: boolean;
  readonly components?:
    | {
        readonly authorFollow?: ReactNode | undefined;
      }
    | undefined;
  readonly onNextPart?: (() => void) | undefined;
  readonly onPrevPart?: (() => void) | undefined;
  readonly onContentClick?: (() => void) | undefined;
}

const DropPart = memo(
  ({
    dropId,
    profile,
    mentionedUsers,
    mentionedWaves,
    referencedNfts,
    partContent,
    partMedias,
    dropTitle,
    createdAt,
    wave,
    size = DropPartSize.MEDIUM,
    totalPartsCount,
    currentPartCount,
    components,
    smallMenuIsShown,
    onNextPart,
    onPrevPart,
    onContentClick,
  }: DropPartProps) => {
    const router = useRouter();
    const isStorm = totalPartsCount && totalPartsCount > 1;
    const showPrevButton = currentPartCount && currentPartCount > 1;
    const showNextButton =
      currentPartCount && totalPartsCount && currentPartCount < totalPartsCount;
    const containerRef = useRef<HTMLDivElement>(null);

    const onQuoteClick = (drop: ApiDrop) => {
      const isDirectMessage =
        (
          drop.wave as {
            chat?:
              | {
                  scope?:
                    | {
                        group?:
                          | { is_direct_message?: boolean | undefined }
                          | undefined;
                      }
                    | undefined;
                }
              | undefined;
          }
        ).chat?.scope?.group?.is_direct_message ?? false;
      router.push(
        getWaveRoute({
          waveId: drop.wave.id,
          serialNo: drop.serial_no,
          isDirectMessage,
          isApp: false,
        }),
        {
          scroll: false,
        }
      );
    };

    return (
      <div
        ref={containerRef}
        className="tw-relative tw-transform tw-overflow-hidden tw-transition-all tw-duration-300 tw-ease-out"
      >
        <div className="tw-relative tw-flex tw-h-full tw-gap-x-3 tw-pt-2">
          <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-self-center sm:tw-self-start">
            <div className={`${smallMenuIsShown && ""} tw-flex tw-gap-x-3`}>
              <DropPfp pfpUrl={profile.pfp} size={size} />
              <div
                className={`tw-flex tw-w-full tw-flex-col ${
                  wave?.id ? "tw-justify-between" : "tw-justify-center"
                } ${
                  size === DropPartSize.SMALL && !wave?.id
                    ? "tw-h-8"
                    : "tw-h-10"
                }`}
              >
                <DropAuthor profile={profile} timestamp={createdAt} size={size}>
                  {components?.authorFollow}
                </DropAuthor>
                <div className="tw-mt-1 tw-inline-flex tw-items-center tw-justify-between">
                  {wave?.id && (
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      href={getWavePathRoute(wave.id)}
                      className="tw-mb-0 tw-pb-0 tw-text-xs tw-text-iron-400 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-50"
                    >
                      <span>{wave.name}</span>
                    </Link>
                  )}
                  {isStorm && (
                    <div className="tw-relative tw-inline-flex">
                      <svg
                        className="tw-mr-2 tw-h-4 tw-w-4 tw-text-[#FEDF89]"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M21 4H3M20 8L6 8M18 12L9 12M15 16L8 16M17 20H12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="tw-text-xs tw-text-iron-50">
                        {currentPartCount} /{" "}
                        <span className="tw-text-iron-400">
                          {totalPartsCount}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              onClick={(e) => {
                if (onContentClick) {
                  const selection = window.getSelection();
                  if (selection?.toString().length) {
                    return;
                  }

                  e.stopPropagation();
                  onContentClick();
                }
              }}
              className={`${onContentClick && "tw-cursor-pointer"} tw-h-full ${
                size === DropPartSize.SMALL ? "tw-ml-[40px]" : "tw-ml-[54px]"
              }`}
            >
              {dropTitle && (
                <p className="tw-mb-0 tw-text-md tw-font-semibold tw-text-iron-100">
                  {dropTitle}
                </p>
              )}
              <div className="tw-inline-flex tw-w-full tw-justify-between tw-space-x-2">
                {onPrevPart && isStorm && (
                  <button
                    disabled={!showPrevButton}
                    className={`${
                      showPrevButton
                        ? "tw-text-iron-300 hover:tw-text-primary-400"
                        : "tw-cursor-default tw-text-iron-700"
                    } tw-rounded-lg tw-border-0 tw-bg-transparent tw-transition tw-duration-300 tw-ease-out`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPrevPart();
                    }}
                  >
                    <svg
                      className="tw-size-5 tw-flex-shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 19.5 8.25 12l7.5-7.5"
                      />
                    </svg>
                  </button>
                )}
                <DropPartContent
                  mentionedUsers={mentionedUsers}
                  mentionedWaves={mentionedWaves}
                  referencedNfts={referencedNfts}
                  partContent={partContent}
                  onQuoteClick={onQuoteClick}
                  currentDropId={dropId}
                  partMedias={partMedias}
                  currentPartCount={currentPartCount ?? 0}
                />
                {onNextPart && isStorm && (
                  <button
                    className={`${
                      showNextButton
                        ? "tw-text-iron-300 hover:tw-text-primary-400"
                        : "tw-cursor-default tw-text-iron-700"
                    } tw-rounded-lg tw-border-0 tw-bg-transparent tw-transition tw-duration-300 tw-ease-out`}
                    disabled={!showNextButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onNextPart();
                    }}
                  >
                    <svg
                      className="tw-size-5 tw-flex-shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.25 4.5 7.5 7.5-7.5 7.5"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

DropPart.displayName = "DropPart";
export default DropPart;
